// @ts-check
import React, { useState } from 'react';
import * as _ from 'lodash';
//utilities
import { messageUtility } from '../utilities/messageUtility';
// icon
import { CopyLeaveIcon } from './icons/CopyLeaveIcon';
import { CopyHoverIcon } from './icons/CopyHoverIcon';

import Tippy from './ReactTippy';

/**
 * @typedef copyToClipboardAreaProps
 * @property {JSX.Element | null} children クリック時に文字列をコピーできるようにする領域
 * @property {string} copyText クリップボードにコピーされる文字列
 * @property {string} className 全体に適用するクラス名
 * @property {string} copiableIconClassName コピーできることを示すアイコンに適用するクラス名
 * @property {object} toolTipProps ツールチップ(Tippy)に適用するprops、指定したものだけデフォルトから上書きする
 * @property {boolean} isShowCopiableIcon クリック領域の末尾にコピーできることを示すアイコンを表示するか
 * @property {boolean} isShowCopiableToolTip クリック領域の末尾にコピーできることを示すツールチップを表示するか
 * @property {boolean} isClickEventOccur クリックイベントが発生するか
 * @property {boolean} isHoverEventOccur ホバーイベントが発生するか
 *
 */

/**
 * @description
 ** クリックすることでクリップボードに文字列をコピーできるエリア
 ** NOTE:本関数はPC版のみ動作を保証する
 **      SP版で使うなら、クリップボードの権限やツールチップの動作など要確認
 * @function
 * @param {copyToClipboardAreaProps} props
 */
export default function CopyToClipboardArea({
  children = null,
  copyText = '',
  className = '',
  toolTipProps = {},
  isShowCopiableIcon = true,
  isShowCopiableToolTip = true,
  isClickEventOccur = true,
  isHoverEventOccur = true,
}) {
  const [isMouseHover, setIsMouseHover] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  /**
   * @description クリックボードに文字列をコピー
   * @function
   * @param {string} text
   */
  const copyToClipboard = text => {
    // @ts-ignore
    global.navigator.clipboard
      .writeText(text)
      .then(() => setIsCopied(true))
      .catch(() => {
        // 失敗しても何もしない。
        // NOTE：クリップボード書き込み権限はアクティブなタブには自動付与される。
        //       このため、この処理は失敗しない想定。
        //       参考：https://developer.mozilla.org/ja/docs/Web/API/Clipboard/writeText
      });
  };

  return (
    <CopieableToolTip
      isCopied={isCopied}
      isShowCopiableToolTip={isShowCopiableToolTip}
      toolTipProps={toolTipProps}
    >
      <span
        className={
          className === ''
            ? 'CopyToClipboardArea'
            : `${'CopyToClipboardArea '}${className}`
        }
        onClick={() => {
          if (isClickEventOccur) {
            copyToClipboard(copyText);
          }
        }}
        onMouseEnter={() => {
          if (isHoverEventOccur) {
            setIsMouseHover(true);
          }
        }}
        onMouseLeave={() => {
          if (isHoverEventOccur) {
            setIsMouseHover(false);
            // マウスカーソルを離した瞬間に、前の文字が見えるのを防止するため少し時間を置く
            window.setTimeout(() => setIsCopied(false), 300);
          }
        }}
      >
        {children}
        <CopiableIcon
          isMouseHover={isMouseHover}
          isShowCopiableIcon={isShowCopiableIcon}
        />
      </span>
    </CopieableToolTip>
  );
}

/**
 * @typedef copiableIconProps
 * @property {boolean} isMouseHover マウスホバーしているか
 * @property {boolean} isShowCopiableIcon アイコンを表示するか
 */

/**
 * @description コピーできることを示すアイコン
 * @function
 * @param {copiableIconProps} copyIconProps
 */
function CopiableIcon({ isMouseHover, isShowCopiableIcon }) {
  return isShowCopiableIcon ? (
    isMouseHover ? (
      <CopyHoverIcon />
    ) : (
      <CopyLeaveIcon />
    )
  ) : null;
}

/**
 * @typedef copieableToolTipProps
 * @property {JSX.Element | null} children 子要素
 * @property {boolean} isCopied コピー済み済み状態か
 * @property {boolean} isShowCopiableToolTip ツールチップを表示するか
 * @property {object} toolTipProps ツールチップ(Tippy)に適用するprops
 */

/**
 * @description コピーできることを示すアイコン
 * @function
 * @param {copieableToolTipProps} copyToolTipProps
 */
function CopieableToolTip({
  children,
  isCopied,
  isShowCopiableToolTip,
  toolTipProps,
}) {
  const defalutProps = {
    arrow: 'true',
    arrowType: 'sharp',
    placement: 'right',
    html: (
      <span style={{ color: '#ffffff' }}>
        {isCopied
          ? messageUtility.getMessage('I-000-252')
          : messageUtility.getMessage('I-000-251')}
      </span>
    ),

    hideOnClick: false,
  };

  const applyProps = _.merge({}, _.cloneDeep(toolTipProps), defalutProps);

  return isShowCopiableToolTip ? (
    // @ts-ignore Tippyの型情報無し
    <Tippy {...applyProps}>{children}</Tippy>
  ) : null;
}



// WEBPACK FOOTER //
// ./src/components/CopyToClipBordArea.jsx